import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle2Icon, CreditCardIcon, HelpCircleIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Spinner } from '@/components/ui/spinner';
import { Input } from '@/components/ui/input';
import { useHelper } from '@/context/HelpSidebarContext';
import useDialog from '@/hooks/useDialog';
import server from '@/services/server';
import { useClub } from '../ClubContext';
import useClubErrors from '../hooks/useClubErrors';
import { useClubConfirm } from '../providers/ClubConfirmProvider';
import ClubSettingsCardHeader from './ClubSettingsCardHeader';

/** Só ASCII imprimível sem espaço — remove ZWSP (U+200B), quebras e invisíveis. */
function sanitizeAsaasApiKeyInput(value) {
    return String(value ?? '').replace(/[^\x21-\x7E]/g, '');
}

const asaasKeySchema = z.object({
    asaasKey: z
        .string()
        .transform(sanitizeAsaasApiKeyInput)
        .pipe(z.string().min(1, 'Informe a chave de API')),
});

function AsaasHelpContent() {
    return (
        <div className="flex flex-col gap-4 text-sm">
            <h2 className="text-base font-semibold">Integração com Asaas</h2>
            <p>
                O Asaas é uma plataforma de pagamentos que permite processar transações de cartão de crédito, PIX, boleto bancário e outras formas de
                pagamento para seus planos de assinatura.
            </p>

            <div>
                <h3 className="mb-2 font-semibold">Por que preciso da chave de API?</h3>
                <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                    <li>Processar pagamentos dos seus planos</li>
                    <li>Gerar cobranças automaticamente</li>
                    <li>Receber notificações de pagamento</li>
                    <li>Gerenciar assinaturas recorrentes</li>
                </ul>
            </div>

            <div>
                <h3 className="mb-2 font-semibold">Importante — Segurança</h3>
                <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                    <li>
                        <strong>Nunca compartilhe</strong> sua chave de API com terceiros
                    </li>
                    <li>
                        A chave é <strong>criptografada</strong> e armazenada com segurança
                    </li>
                    <li>
                        Se suspeitar de uso indevido, <strong>gere uma nova chave</strong> no Asaas
                    </li>
                </ul>
            </div>

            <div>
                <h3 className="mb-2 font-semibold">Como obter sua chave de API?</h3>
                <ol className="list-decimal space-y-1 pl-5 text-muted-foreground">
                    <li>Acesse sua conta no Asaas</li>
                    <li>Vá em Configurações → Integrações</li>
                    <li>Clique em Gerar nova chave de API</li>
                    <li>Copie a chave (ela começa com $aact_)</li>
                    <li>Cole no campo de configuração</li>
                </ol>
            </div>

            <p className="text-muted-foreground">
                A chave sempre começa com <strong>$aact_</strong> seguido de caracteres. Exemplo:{' '}
                <code className="rounded bg-muted px-1">$aact_YTWYTE0EN...</code>
            </p>
        </div>
    );
}

export function DialogPaymentIntegration({ data, mutate, open, onOpenChange }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const confirm = useClubConfirm();
    const { chargeHelp } = useHelper();
    const [removing, setRemoving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const form = useForm({
        resolver: zodResolver(asaasKeySchema),
        defaultValues: {
            asaasKey: '',
        },
    });

    useEffect(() => {
        if (!open) return;

        setTestResult(null);
        form.reset({
            asaasKey: '',
        });
    }, [open, form]);

    const onCreate = async values => {
        try {
            await confirm({
                title: 'Adicionar chave de integração do Asaas',
                description: 'Lembre-se: se perder essa chave, será preciso gerar uma nova no Asaas. Deseja continuar?',
                confirmationText: 'Configurar',
            });

            await server.post('club/payment/asaas', values);
            enqueueSnackbar('Adicionado com sucesso.');
            await mutate();
            onOpenChange(false);
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        }
    };

    const onRemove = async () => {
        try {
            await confirm({
                title: 'Remover chave de integração do Asaas',
                description: 'Lembre-se: se perder essa chave, será preciso gerar uma nova no Asaas. Deseja continuar?',
                confirmationText: 'Remover',
                confirmationButtonProps: { variant: 'destructive' },
            });

            setRemoving(true);
            await server.delete('club/payment/asaas');
            enqueueSnackbar('Chave Asaas removida com sucesso.');
            await mutate();
            onOpenChange(false);
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        } finally {
            setRemoving(false);
        }
    };

    const onTestKey = async () => {
        try {
            setTesting(true);
            setTestResult(null);
            const { data: response } = await server.get('club/payment/asaas/account');
            const account = response?.data || response;
            setTestResult({ ok: true, account });
            enqueueSnackbar(response?.message || 'Chave Asaas válida.');
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                'Não foi possível consultar a conta no Asaas com a chave salva.';
            setTestResult({ ok: false, message });
        } finally {
            setTesting(false);
        }
    };

    const { errors, isSubmitting } = form.formState;
    const hasKey = Boolean(data?.hasAsaasKey);
    const busy = isSubmitting || removing || testing;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader className="flex-row items-start justify-between gap-4 space-y-0">
                    <div className="flex flex-col gap-1.5">
                        <DialogTitle>Integração com Asaas</DialogTitle>
                        <DialogDescription>Configure a chave de API para processar pagamentos.</DialogDescription>
                    </div>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Ajuda"
                        onClick={() => chargeHelp(<AsaasHelpContent />)}
                    >
                        <HelpCircleIcon className="size-4" />
                    </Button>
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    {hasKey ? (
                        <>
                            <div className="rounded-xl border border-green-200 bg-green-50 p-4 dark:border-green-900 dark:bg-green-950/30">
                                <div className="flex items-start gap-3">
                                    <CheckCircle2Icon className="mt-0.5 size-6 text-green-600 dark:text-green-400" />
                                    <div>
                                        <p className="font-semibold text-green-800 dark:text-green-300">Integração ativa</p>
                                        <p className="text-sm text-muted-foreground">Sua chave API está configurada e funcionando.</p>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <p className="mb-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">Chave API</p>
                                    <p className="rounded-lg bg-muted p-3 font-mono text-sm break-all">$aact_••••••••••••••••••••••••••</p>
                                    <p className="mt-1 text-xs text-muted-foreground">Por segurança, a chave completa está oculta.</p>
                                </div>
                            </div>
                            <Alert>
                                <AlertDescription>Para alterar a chave, remova a atual e configure uma nova.</AlertDescription>
                            </Alert>
                            <Button type="button" variant="outline" disabled={busy || testing} onClick={onTestKey}>
                                {testing ? <Spinner className="size-4" /> : null}
                                Testar chave
                            </Button>
                            {testResult?.ok ? (
                                <Alert>
                                    <AlertDescription>
                                        <p className="font-medium text-foreground">A conta Asaas respondeu.</p>
                                        <ul className="mt-2 space-y-1">
                                            {testResult.account?.name ? <li>Nome: {testResult.account.name}</li> : null}
                                            {testResult.account?.email ? <li>E-mail: {testResult.account.email}</li> : null}
                                            {testResult.account?.cpfCnpj ? <li>Documento: {testResult.account.cpfCnpj}</li> : null}
                                            {testResult.account?.city || testResult.account?.state ? (
                                                <li>
                                                    Local: {[testResult.account.city, testResult.account.state].filter(Boolean).join(' / ')}
                                                </li>
                                            ) : null}
                                        </ul>
                                    </AlertDescription>
                                </Alert>
                            ) : null}
                            {testResult && !testResult.ok ? (
                                <Alert variant="error">
                                    <AlertDescription>{testResult.message}</AlertDescription>
                                </Alert>
                            ) : null}
                        </>
                    ) : (
                        <>
                            <form id="asaas-form" onSubmit={form.handleSubmit(onCreate)} className="flex flex-col gap-4">
                                <FieldGroup>
                                    <Field data-invalid={Boolean(errors.asaasKey)}>
                                        <FieldLabel htmlFor="asaas-key">Chave de API do Asaas</FieldLabel>
                                        <Controller
                                            name="asaasKey"
                                            control={form.control}
                                            render={({ field }) => (
                                                <Input
                                                    id="asaas-key"
                                                    autoComplete="off"
                                                    spellCheck={false}
                                                    placeholder="ex: $aact_YTWYTE0EN..."
                                                    aria-invalid={Boolean(errors.asaasKey)}
                                                    name={field.name}
                                                    ref={field.ref}
                                                    onBlur={field.onBlur}
                                                    value={field.value ?? ''}
                                                    onChange={event =>
                                                        field.onChange(
                                                            sanitizeAsaasApiKeyInput(event.target.value),
                                                        )
                                                    }
                                                    onPaste={event => {
                                                        event.preventDefault();
                                                        const pasted =
                                                            event.clipboardData?.getData('text') ?? '';
                                                        field.onChange(sanitizeAsaasApiKeyInput(pasted));
                                                    }}
                                                />
                                            )}
                                        />
                                        <FieldError>{errors.asaasKey?.message}</FieldError>
                                        <p className="text-xs text-muted-foreground">
                                            Cole a chave do Asaas (começa com $aact_). Caracteres invisíveis e
                                            quebras de linha são removidos automaticamente.
                                        </p>
                                    </Field>
                                </FieldGroup>
                            </form>
                            <Alert>
                                <AlertDescription>
                                    Clique no ícone de ajuda (?) no topo para ver instruções detalhadas sobre como obter sua chave.
                                </AlertDescription>
                            </Alert>
                        </>
                    )}
                </div>

                <DialogFooter>
                    <Button type="button" variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
                        Cancelar
                    </Button>
                    {hasKey ? (
                        <Button type="button" variant="destructive" disabled={busy} onClick={onRemove}>
                            {removing ? <Spinner className="size-4" /> : <Trash2Icon className="size-4" />}
                            Remover
                        </Button>
                    ) : (
                        <Button type="submit" form="asaas-form" disabled={busy}>
                            {isSubmitting ? <Spinner className="size-4" /> : <PlusIcon className="size-4" />}
                            Configurar integração
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function IntegracaoAsaasClub({ data, mutate }) {
    const isReseller = data?.functions?.includes('reseller');
    const { open, handleClose } = useDialog('#payment-asaas');
    const { health } = useClub();
    const attention = isReseller && health?.checks?.asaasKey?.ok === false;

    if (!isReseller) {
        return null;
    }

    return (
        <>
            <Card>
                <CardHeader>
                    <ClubSettingsCardHeader
                        icon={CreditCardIcon}
                        title="Integrador de pagamentos — Asaas"
                        description="Processamento de assinaturas via Asaas."
                        attention={attention}
                    />
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                    <span className="text-muted-foreground">Situação</span>
                    <p className="font-medium">{data?.hasAsaasKey ? 'Habilitado' : 'Sem configuração'}</p>
                </CardContent>
                <CardFooter>
                    <Button variant="outline" size="sm" render={<Link to="#payment-asaas" />}>
                        <CreditCardIcon className="size-4" />
                        {data?.hasAsaasKey ? 'Ver configuração' : 'Configurar'}
                    </Button>
                </CardFooter>
            </Card>

            <DialogPaymentIntegration
                data={data}
                mutate={mutate}
                open={open}
                onOpenChange={isOpen => !isOpen && handleClose()}
            />
        </>
    );
}
